import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useParams, useSearchParams } from 'react-router-dom';
import config from '../config';
import AvailabilityPicker, { AvailabilitySlot } from '../components/AvailabilityPicker';

interface AvailabilityContext {
  certificate_type: string | null;
  property_address: string | null;
  expiry_date: string | null;
  tenant_name: string | null;
  already_submitted: boolean;
  submitted_slots: AvailabilitySlot[];
  audience?: 'tenant' | 'provider' | 'provider_counter';
  provider_slots?: AvailabilitySlot[];
  counter_slots?: AvailabilitySlot[];
}

const slotLabel = (s: AvailabilitySlot) =>
  `${new Date(s.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}, ${s.start_time}\u2013${s.end_time}`;

const AvailabilityPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [pickIndexes, setPickIndexes] = useState<number[]>(() => {
    const p = searchParams.get('pick');
    return p !== null && /^\d+$/.test(p) ? [Number(p)] : [];
  });
  const toggleIndex = (i: number) => setPickIndexes(prev => (prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]));
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [context, setContext] = useState<AvailabilityContext | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!token) return;
    fetch(`${config.apiUrl}/api/availability/${token}`)
      .then(res => res.json())
      .then(data => {
        if (!data.success) {
          setError(data.error || 'This link is invalid or has expired.');
        } else {
          setContext(data);
          if (data.already_submitted && data.submitted_slots?.length) {
            setSlots(data.submitted_slots);
          }
        }
      })
      .catch(() => setError('Could not load this page. Please try again later.'))
      .finally(() => setLoading(false));
  }, [token]);

  const expiryDate = useMemo(() => {
    if (!context?.expiry_date) return null;
    const d = new Date(context.expiry_date);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [context]);

  const daysUntilExpiry = useMemo(() => {
    if (!expiryDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  }, [expiryDate]);

  const isCounter = context?.audience === 'provider_counter';
  const providerSlots = context?.audience === 'tenant' ? (context.provider_slots || []) : isCounter ? (context?.counter_slots || []) : [];
  const [declined, setDeclined] = useState(false);
  // The tenant either picks one of the provider's times or proposes others, never both at once.
  const [proposing, setProposing] = useState(false);
  const showCalendar = !isCounter && (providerSlots.length === 0 || proposing);

  const handleDecline = async () => {
    if (!token) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/availability/${token}/decline`, { method: 'POST' });
      const data = await res.json();
      if (data.success) setDeclined(true);
      else setError(data.error || 'Something went wrong. Please try again.');
    } catch {
      setError('Could not send your reply. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccept = async () => {
    if (!token || pickIndexes.length === 0) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/availability/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slots: pickIndexes.map(i => providerSlots[i]).filter(Boolean) }),
      });
      const data = await res.json();
      if (data.success) setAccepted(true);
      else setError(data.error || 'Something went wrong. Please try again.');
    } catch {
      setError('Could not confirm this time. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!token || slots.length === 0) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/availability/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slots, notes: notes || undefined }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Could not submit your availability. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 10, px: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (accepted) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 10, px: 3, textAlign: 'center' }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56, mb: 2 }} />
        <Typography variant="h5" gutterBottom>Thanks, your times are noted</Typography>
        <Typography color="text.secondary">
          You will get a confirmation email shortly.
        </Typography>
      </Box>
    );
  }

  if (declined) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 10, px: 3, textAlign: 'center' }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56, mb: 2 }} />
        <Typography variant="h5" gutterBottom>Thanks, your reply is noted</Typography>
        <Typography color="text.secondary">We will be in touch to find another way forward.</Typography>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 10, px: 3, textAlign: 'center' }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56, mb: 2 }} />
        <Typography variant="h5" gutterBottom>Thanks — availability received</Typography>
        <Typography color="text.secondary">
          We'll be in touch to confirm a time for the inspection visit.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', my: { xs: 3, sm: 8 }, px: 3 }}>
      {providerSlots.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>{isCounter ? 'Select the dates that suit you' : 'Select the times that suit you'}</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {context?.property_address ? `${context.property_address}: ` : ''}{isCounter ? 'the tenant is available on any of these dates' : 'our engineer can attend at any of these times'}. Select as many as you like, then confirm.
          </Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {providerSlots.map((s, i) => (
              <Button key={i} variant={pickIndexes.includes(i) ? 'contained' : 'outlined'} disabled={proposing} onClick={() => toggleIndex(i)} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                {slotLabel(s)}
              </Button>
            ))}
          </Stack>
          <Button variant="contained" size="large" fullWidth disabled={submitting || proposing || pickIndexes.length === 0} onClick={handleAccept} sx={{ mb: 1.5 }}>
            {submitting ? <CircularProgress size={22} /> : 'Confirm'}
          </Button>
          {isCounter ? (
            <Button fullWidth disabled={submitting} onClick={handleDecline}>None of these work for me</Button>
          ) : proposing ? (
            <Button fullWidth onClick={() => { setProposing(false); setSlots([]); setNotes(''); }}>Go back to the provider's availability</Button>
          ) : (
            <Button fullWidth onClick={() => { setPickIndexes([]); setProposing(true); }}>None of these work? Suggest another time</Button>
          )}
        </Box>
      )}

      {showCalendar && (<>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: context?.audience === 'tenant' ? 3 : 1 }}>
        <EventAvailableIcon color="primary" />
        <Typography variant="h5">Tell us your availability</Typography>
      </Stack>
      {context?.audience !== 'tenant' && (
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {context?.property_address ? `${context.property_address} — ` : ''}
          {expiryDate && daysUntilExpiry !== null
            ? `the ${context?.certificate_type || 'certificate'} expires on ${expiryDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}, which is in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}.`
            : 'we need to arrange an inspection visit.'}{' '}
          Pick the dates and times that work for you below.
        </Typography>
      )}

      <AvailabilityPicker slots={slots} onSlotsChange={setSlots} notes={notes} onNotesChange={setNotes} expiryDate={expiryDate} markedSlots={providerSlots} />

      <Button
        variant="contained" size="large" fullWidth
        disabled={slots.length === 0 || submitting}
        onClick={handleSubmit}
      >
        {submitting ? <CircularProgress size={22} /> : 'Submit my availability'}
      </Button>
      </>)}
    </Box>
  );
};

export default AvailabilityPage;
